/**
 * AdminPage - Container component for admin dashboard
 *
 * Manages admin data and delegates UI to AdminPageView.
 * Handles system health monitoring and admin operations.
 */
import { useReducer, useEffect } from 'react';
import AdminPageView from './AdminPageView';
import { fetchSystemHealth, fetchAdminMetrics, fetchActiveProvider, switchActiveProvider, clearActiveProvider, fetchLsPlanCredits, updateLsPlanCredits } from './admin.api';
import type { SystemHealth, AdminMetrics } from './admin.schema';
import type { ActiveProviderInfo, LsPlanCreditsConfig } from './admin.api'; // task-30/31: provider badge + LS credits state

interface AdminState {
  systemHealth: SystemHealth | null;
  metrics: AdminMetrics | null;
  loading: boolean;
  error: string | null;
  activeProvider: ActiveProviderInfo | null; // task-30: default provider + supported list for badge
  lsPlanCredits: LsPlanCreditsConfig | null; // task-31: per-plan LS credit config
}

type AdminAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ADMIN_DATA'; payload: { systemHealth: SystemHealth; metrics: AdminMetrics } }
  | { type: 'SET_ACTIVE_PROVIDER'; payload: ActiveProviderInfo } // task-30: provider info action
  | { type: 'SET_LS_PLAN_CREDITS'; payload: LsPlanCreditsConfig } // task-31: LS credits config action
  | { type: 'RESET_STATE' };

const adminReducer=(state:AdminState,action:AdminAction):AdminState=>{
    switch(action.type){
        case'SET_LOADING':return{...state,loading:action.payload};
        case'SET_ERROR':return{...state,error:action.payload,loading:false};
        case'SET_ADMIN_DATA':return{...state,systemHealth:action.payload.systemHealth,metrics:action.payload.metrics,error:null,loading:false};
        case'SET_ACTIVE_PROVIDER':return{...state,activeProvider:action.payload}; // task-30: store provider info without touching other state
        case'SET_LS_PLAN_CREDITS':return{...state,lsPlanCredits:action.payload}; // task-31: store LS credits config without touching other state
        case'RESET_STATE':return{systemHealth:null,metrics:null,loading:true,error:null,activeProvider:null,lsPlanCredits:null};
        default:return state;
    }
};

const AdminPage=()=>{
    console.log('AdminPage is running');
    const[state,dispatch]=useReducer(adminReducer,{systemHealth:null,metrics:null,loading:true,error:null,activeProvider:null,lsPlanCredits:null});
    const envKey=import.meta.env.VITE_QreditCharge_API_KEY as string|undefined;
    const lsKey=typeof window!=='undefined'?window.localStorage?.getItem('QreditCharge_API_KEY')||undefined:undefined;
    const apiKeyMissing=!envKey&&!lsKey;
    useEffect(()=>{
        const loadAdminData=async()=>{
            try{
                // task-30: fetch provider info alongside health + metrics so the badge renders on load
                // task-31: fetch LS plan credits config at the same time — non-fatal if it fails
                const[healthData,metricsData,providerInfo,lsCredits]=await Promise.all([
                    fetchSystemHealth(),
                    fetchAdminMetrics(),
                    fetchActiveProvider().catch(()=>null),
                    fetchLsPlanCredits().catch(()=>null)
                ]);
                dispatch({type:'SET_ADMIN_DATA',payload:{systemHealth:healthData,metrics:metricsData}});
                if(providerInfo) dispatch({type:'SET_ACTIVE_PROVIDER',payload:providerInfo}); // provider fetch is non-fatal
                if(lsCredits) dispatch({type:'SET_LS_PLAN_CREDITS',payload:lsCredits}); // LS credits fetch is non-fatal
            }catch(err){
                dispatch({type:'SET_ERROR',payload:err instanceof Error?err.message:'Failed to load admin data'});
            }finally{
                dispatch({type:'SET_LOADING',payload:false});
            }
        };
        loadAdminData();
    },[]);
    // task-30: handler called when the admin selects a different provider from the dashboard selector.
    // Calls PATCH /api/admin/provider and updates local state on success so the badge refreshes instantly.
    const handleChangeProvider=async(provider:string)=>{
        try{
            const updated=await switchActiveProvider(provider);
            dispatch({type:'SET_ACTIVE_PROVIDER',payload:updated}); // optimistic local update — no refetch needed
        }catch(err){
            // non-fatal: log but don't block dashboard; badge retains prior value
            console.error('[AdminPage] provider switch failed:', err instanceof Error?err.message:String(err));
        }
    };
    // task-45: handler called when the admin clicks "Reset to env default" in the provider card.
    // Calls DELETE /api/admin/provider, clears the DB doc, and updates the badge to source='env'.
    const handleResetProvider=async()=>{
        try{
            const updated=await clearActiveProvider(); // removes persisted DB doc; backend falls back to env-var
            dispatch({type:'SET_ACTIVE_PROVIDER',payload:updated}); // badge switches to env-origin label
        }catch(err){
            // non-fatal: log but don't crash dashboard; badge retains prior value so operator knows to retry
            console.error('[AdminPage] provider reset failed:', err instanceof Error?err.message:String(err));
        }
    };
    // task-31: handler called when the admin saves updated LS plan credit amounts.
    // Calls PATCH /api/admin/ls-plan-credits and updates local state on success.
    const handleUpdateLsPlanCredits=async(updates:Partial<{default:number;plans:Partial<Record<'basic'|'premium'|'enterprise',number>>}>)=>{
        try{
            const updated=await updateLsPlanCredits(updates);
            dispatch({type:'SET_LS_PLAN_CREDITS',payload:updated}); // reflect confirmed values from server
        }catch(err){
            console.error('[AdminPage] LS plan credits update failed:', err instanceof Error?err.message:String(err));
            throw err; // re-throw so the UI can show an error message
        }
    };
    console.log('AdminPage is returning admin page');
    return(<AdminPageView systemHealth={state.systemHealth}metrics={state.metrics}loading={state.loading}error={state.error}apiKeyMissing={apiKeyMissing}activeProvider={state.activeProvider}onChangeProvider={handleChangeProvider}onResetProvider={handleResetProvider}lsPlanCredits={state.lsPlanCredits}onUpdateLsPlanCredits={handleUpdateLsPlanCredits}/>);
};

export default AdminPage;
